// ============================================================
//  src/admin/pages/AdminUserDetailPage.js
// ============================================================
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { getUser, userAction, adminListPlans } from '../api/adminClient';
import { Badge, isFlagTrue } from '../components/UI';
import DataTable from '../components/DataTable';
import { useConfirm } from '../components/ConfirmModal';

const STATUS_LABEL = { 0: 'Unverified', 1: 'Active', 2: 'Suspended' };
const STATUS_TONE  = { 0: 'warning', 1: 'success', 2: 'danger' };

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d.replace(' ', 'T'));
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}
function fmtNaira(n) { return '₦' + (Number(n) || 0).toLocaleString('en-NG', { maximumFractionDigits: 0 }); }

export default function AdminUserDetailPage() {
  const { sId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([getUser(sId), adminListPlans()])
      .then(([u, p]) => {
        setData(u.data);
        setPlans(p.data.plans.filter(pl => isFlagTrue(pl.plIsActive)));
        setSelectedPlan(u.data.subscription?.plSlug || '');
      })
      .catch(() => toast.error('Could not load user'))
      .finally(() => setLoading(false));
  }, [sId]);

  useEffect(() => { load(); }, [load]);

  async function doAction(action, extra = {}) {
    setBusy(true);
    try {
      const res = await userAction({ action, sId, ...extra });
      toast.success(res.data.message || 'Done');
      if (action === 'reset_password' && res.data.new_password) {
        toast(`Temporary password: ${res.data.new_password}`, { duration: 10000, icon: '🔑' });
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleBlock() {
    const ok = await confirm({
      title: 'Block this user?',
      message: 'They will be logged out immediately and unable to sign in until unblocked.',
      confirmText: 'Block User',
      tone: 'danger',
    });
    if (ok) doAction('block');
  }

  async function handleResetPassword() {
    const ok = await confirm({
      title: 'Reset password?',
      message: "This generates a new temporary password for the user. Their current password will stop working right away.",
      confirmText: 'Reset Password',
      tone: 'danger',
    });
    if (ok) doAction('reset_password');
  }

  if (loading) return <div className="bf-empty"><span className="spinner spinner-dark" /> Loading user…</div>;
  if (!data) return <div className="bf-empty">User not found.</div>;

  const { user, bots, subscription, payments } = data;

  const botColumns = [
    { key: 'name', label: 'Name', primary: true, render: b => b.bName },
    { key: 'users', label: 'Users', render: b => b.totalUsers },
    {
      key: 'status', label: 'Status',
      render: b => isFlagTrue(b.bAdminSuspended)
        ? <Badge tone="danger">Suspended</Badge>
        : isFlagTrue(b.bIsActive) ? <Badge tone="success">Active</Badge> : <Badge tone="muted">Inactive</Badge>,
    },
    { key: 'created', label: 'Created', render: b => fmtDate(b.bCreatedAt) },
  ];

  const paymentColumns = [
    { key: 'plan', label: 'Plan', primary: true, render: p => p.plName },
    { key: 'amount', label: 'Amount', render: p => fmtNaira(p.payAmount) },
    { key: 'status', label: 'Status', render: p => p.payStatus },
    { key: 'date', label: 'Date', render: p => fmtDate(p.payCreatedAt) },
  ];

  return (
    <div className="bf-page">
      <Link to="/bot-father/users" className="bf-back-link">← Back to Users</Link>
      <div className="bf-detail-header">
        <div>
          <div className="bf-detail-name">{user.sFname} {user.sLname}</div>
          <div className="bf-detail-meta">{user.sEmail} · {user.sPhone || 'No phone'}</div>
        </div>
        <Badge tone={STATUS_TONE[user.sStatus]}>{STATUS_LABEL[user.sStatus]}</Badge>
      </div>

      {/* Actions */}
      <div className="bf-card">
        <div className="bf-card-hdr">Actions</div>
        <div className="bf-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div className="bf-btn-row">
            {Number(user.sStatus) === 2 ? (
              <button className="btn btn-primary" disabled={busy} onClick={() => doAction('unblock')}>Unblock User</button>
            ) : (
              <button className="bf-btn-danger-outline" disabled={busy} onClick={handleBlock}>Block User</button>
            )}

            {Number(user.sStatus) === 0 && (
              <button className="btn btn-outline" disabled={busy} onClick={() => doAction('verify_email')}>Verify Email</button>
            )}

            <button className="btn btn-outline" disabled={busy} onClick={handleResetPassword}>
              Reset Password
            </button>
          </div>

          {/* Change plan */}
          <div className="bf-flex-wrap" style={{ alignItems: 'center' }}>
            <label style={{ fontSize: 13, fontWeight: 700 }}>Plan:</label>
            <select className="bf-select" value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
              {plans.map(p => <option key={p.plSlug} value={p.plSlug}>{p.plName} ({fmtNaira(p.plPrice)})</option>)}
            </select>
            <button className="btn btn-primary" disabled={busy || selectedPlan === subscription?.plSlug}
                    onClick={() => doAction('change_plan', { plan_slug: selectedPlan })}>
              Update Plan
            </button>
          </div>
        </div>
      </div>

      {/* Current subscription */}
      <div className="bf-card">
        <div className="bf-card-hdr">Subscription</div>
        <div className="bf-card-body">
          {subscription ? (
            <div style={{ fontSize: 13.5 }}>
              <div><strong>{subscription.plName}</strong> — {fmtNaira(subscription.plPrice)}</div>
              <div className="bf-muted" style={{ marginTop: 4 }}>
                Started {fmtDate(subscription.subStartedAt)}
                {subscription.subExpiresAt && ` · Expires ${fmtDate(subscription.subExpiresAt)}`}
              </div>
            </div>
          ) : <div className="bf-muted" style={{ fontSize: 13 }}>No active subscription.</div>}
        </div>
      </div>

      {/* Bots */}
      <div className="bf-card">
        <div className="bf-card-hdr">Bots ({bots.length})</div>
        <div className="bf-card-body-flush">
          <DataTable
            columns={botColumns}
            rows={bots}
            keyField="bId"
            onRowClick={b => navigate(`/bot-father/bots/${b.bId}`)}
            emptyMessage="No bots created yet."
          />
        </div>
      </div>

      {/* Payment history */}
      <div className="bf-card">
        <div className="bf-card-hdr">Payment History</div>
        <div className="bf-card-body-flush">
          <DataTable
            columns={paymentColumns}
            rows={payments}
            keyField="payId"
            emptyMessage="No payments yet."
          />
        </div>
      </div>
    </div>
  );
}